import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { resizeImageForEmail } from "@/lib/imageResize";
import { Trash2, Download, Mail, Search, Send, Eye, Users, Loader2, CheckCircle2, AlertCircle, History, Image as ImageIcon, PlayCircle, Pencil, X, Save, FileText, Plus, Copy, CalendarClock } from "lucide-react";
import BlogBlockEditor from "@/components/admin/BlogBlockEditor";
import { emptyBlock, blocksToContent, serializeBlocks } from "@/lib/blogBlocks";

/* Datetime helpers so <input type="datetime-local"> can round-trip the ISO
   strings we store on the server. Input needs "YYYY-MM-DDTHH:mm" without TZ. */
const isoToLocalInput = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
};
const localInputToIso = (v) => {
    if (!v) return "";
    try {
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return "";
        return d.toISOString();
    } catch { return ""; }
};

const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
};

export default function AdminNewsletter() {
    const [tab, setTab] = useState("subs");
    const [pendingOpenDraft, setPendingOpenDraft] = useState(null);
    return (
        <div className="max-w-5xl">
            <h1 className="font-display text-3xl font-bold text-stone-900 flex items-center gap-2 mb-4">
                <Mail className="w-6 h-6 text-[hsl(var(--copper))]" /> Newsletter
            </h1>
            <div className="flex gap-1 mb-6 border-b border-stone-200" data-testid="newsletter-tabs">
                <TabButton active={tab === "subs"} onClick={() => setTab("subs")} icon={Users} label="Subscritores" testid="newsletter-tab-subs" />
                <TabButton active={tab === "send"} onClick={() => setTab("send")} icon={Send} label="Enviar campanha" testid="newsletter-tab-send" />
                <TabButton active={tab === "history"} onClick={() => setTab("history")} icon={History} label="Histórico" testid="newsletter-tab-history" />
            </div>
            {tab === "subs" && <Subscribers />}
            {tab === "send" && <SendCampaign onSent={() => setTab("history")} pendingOpenDraft={pendingOpenDraft} onDraftConsumed={() => setPendingOpenDraft(null)} />}
            {tab === "history" && <History_ onDuplicated={(newDraftId) => { setPendingOpenDraft(newDraftId); setTab("send"); }} />}
        </div>
    );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className={`px-4 py-2.5 flex items-center gap-2 text-sm border-b-2 -mb-px transition ${
                active
                    ? "border-[hsl(var(--copper))] text-[hsl(var(--forest))] font-semibold"
                    : "border-transparent text-stone-500 hover:text-stone-800"
            }`}>
            <Icon className="w-4 h-4" /> {label}
        </button>
    );
}

/* ---------- SUBSCRIBERS ---------- */
const SUB_STATUSES = ["active", "pending", "unsubscribed", "bounced", "complained", "blocked"];

function Subscribers() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // subscriber being edited
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/newsletter");
            setItems(data || []);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const ql = q.trim().toLowerCase();
        if (!ql) return items;
        return items.filter((s) => (s.email || "").toLowerCase().includes(ql) || (s.source || "").toLowerCase().includes(ql));
    }, [items, q]);

    const del = async (id) => {
        if (!window.confirm("Remover este subscritor?")) return;
        await api.delete(`/admin/newsletter/${id}`);
        toast.success("Removido"); load();
    };

    const save = async () => {
        if (!editing) return;
        const clean = (editing.email || "").trim().toLowerCase();
        if (!clean || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) return toast.error("Email inválido");
        setSaving(true);
        try {
            await api.patch(`/admin/newsletter/${editing.id}`, {
                email: clean,
                source: (editing.source || "").trim() || "manual",
                status: editing.status,
            });
            toast.success("Subscritor atualizado");
            setEditing(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao atualizar"); }
        finally { setSaving(false); }
    };

    const downloadCsv = async () => {
        try {
            const resp = await api.get("/admin/newsletter/export.csv", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url; a.download = "newsletter-subscribers.csv";
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch { toast.error("Falha ao exportar"); }
    };

    return (
        <div>
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                <p className="text-sm text-stone-500">
                    {loading ? "A carregar…" : <>{items.length} subscritor{items.length === 1 ? "" : "es"} no total.</>}
                </p>
                <button onClick={downloadCsv} className="btn-copper !py-2 !px-4 text-sm" data-testid="newsletter-export">
                    <Download className="w-4 h-4" /> Exportar CSV
                </button>
            </div>
            <div className="mb-4 relative">
                <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={q} onChange={(e) => setQ(e.target.value)}
                    placeholder="Procurar por email ou origem…"
                    className="w-full pl-9 pr-4 py-2 border border-stone-300 rounded text-sm"
                    data-testid="newsletter-search" />
            </div>
            <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
                {filtered.map((s) => (
                    <div key={s.id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`newsletter-row-${s.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-stone-900 truncate">{s.email}</div>
                            <div className="text-xs text-stone-500 flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-stone-100 uppercase tracking-wide text-[10px]">{s.source || "—"}</span>
                                <SubStatusPill status={s.status} />
                                <span>{fmt(s.created_at)}</span>
                            </div>
                        </div>
                        <div className="flex gap-2 self-start sm:self-auto">
                            <button onClick={() => setEditing({ ...s })} className="text-xs px-3 py-1.5 border border-stone-300 rounded flex items-center gap-1"
                                data-testid={`newsletter-edit-${s.id}`}>
                                <Pencil className="w-3.5 h-3.5" /> Editar
                            </button>
                            <button onClick={() => del(s.id)} className="text-xs px-3 py-1.5 border border-red-200 text-red-600 rounded flex items-center gap-1"
                                data-testid={`newsletter-del-${s.id}`}>
                                <Trash2 className="w-3.5 h-3.5" /> Remover
                            </button>
                        </div>
                    </div>
                ))}
                {!loading && filtered.length === 0 && (
                    <div className="p-10 text-center text-stone-500">
                        {items.length === 0
                            ? "Ainda sem subscritores. Assim que alguém subscrever no Blog, aparece aqui."
                            : "Nenhum subscritor corresponde à pesquisa."}
                    </div>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-stone-950/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
                    <div className="bg-white rounded-xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="newsletter-edit-modal">
                        <div className="p-5 border-b border-stone-100 flex items-center justify-between">
                            <h2 className="font-display font-bold text-lg flex items-center gap-2"><Pencil className="w-4 h-4 text-[hsl(var(--copper))]" /> Editar subscritor</h2>
                            <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-3 text-sm">
                            <label className="block">
                                <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Email</span>
                                <input type="email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                                    className="w-full border border-stone-300 rounded p-2" data-testid="newsletter-edit-email" />
                                <p className="text-[11px] text-stone-500 mt-1">Corrigir tipos ou domínios errados. O sistema recusa se o novo email já existir noutro subscritor.</p>
                            </label>
                            <label className="block">
                                <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Origem</span>
                                <input value={editing.source || ""} onChange={(e) => setEditing({ ...editing, source: e.target.value })}
                                    className="w-full border border-stone-300 rounded p-2" placeholder="ex.: popup, orcamento, blog-list"
                                    data-testid="newsletter-edit-source" />
                            </label>
                            <label className="block">
                                <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Estado</span>
                                <select value={editing.status || "active"} onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                                    className="w-full border border-stone-300 rounded p-2 bg-white" data-testid="newsletter-edit-status">
                                    {SUB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <p className="text-[11px] text-stone-500 mt-1">Apenas <b>active</b> recebe campanhas. Alterar para <b>unsubscribed</b> regista o timestamp automaticamente.</p>
                            </label>
                            <div className="text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded p-2">
                                <div><b>ID:</b> {editing.id}</div>
                                <div><b>Consent:</b> {editing.consent_given ? "sim" : "não"} · <b>Criado:</b> {fmt(editing.created_at)}</div>
                                {editing.unsubscribed_at && <div><b>Unsub em:</b> {fmt(editing.unsubscribed_at)}</div>}
                            </div>
                            <div className="flex gap-2 pt-1">
                                <button onClick={save} disabled={saving} className="btn-copper !py-2 !px-4 text-sm flex-1 justify-center disabled:opacity-50"
                                    data-testid="newsletter-edit-save">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar alterações
                                </button>
                                <button onClick={() => setEditing(null)} className="px-4 py-2 border border-stone-300 rounded text-sm">Cancelar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function SubStatusPill({ status }) {
    if (!status || status === "active") return <span className="text-[10px] uppercase bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5">active</span>;
    if (status === "unsubscribed") return <span className="text-[10px] uppercase bg-stone-200 text-stone-700 rounded-full px-2 py-0.5">unsubscribed</span>;
    if (status === "bounced" || status === "complained" || status === "blocked") return <span className="text-[10px] uppercase bg-red-100 text-red-800 rounded-full px-2 py-0.5">{status}</span>;
    return <span className="text-[10px] uppercase bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">{status}</span>;
}

/* ---------- SEND CAMPAIGN ---------- */
function SendCampaign({ onSent, pendingOpenDraft, onDraftConsumed }) {
    const emptyForm = () => ({
        id: null,
        subject: "",
        preheader: "",
        sources: [],
        blocks: [emptyBlock("paragraph")],
        scheduled_at: "",
    });
    const [form, setForm] = useState(emptyForm);
    const [savingDraft, setSavingDraft] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [sending, setSending] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    const [testEmail, setTestEmail] = useState("ola@ohomemdaobra.pt");
    const [count, setCount] = useState(null);
    const [sources, setSources] = useState([]);
    // Auto-save state: `dirty` marks that local changes are unsaved,
    // `autoSavedAt` shows the last successful auto-save time, `autoSaving`
    // fires the spinner. The auto-save effect debounces the writes so a
    // burst of keystrokes results in a single PUT.
    const [dirty, setDirty] = useState(false);
    const [autoSaving, setAutoSaving] = useState(false);
    const [autoSavedAt, setAutoSavedAt] = useState(null);
    // Guard: skip the first `dirty` set on load so `openDraft` / `emptyForm`
    // don't trigger an immediate re-save of a just-loaded state.
    const skipNextDirty = useRef(true);

    // Fetch source list + total once, and refresh drafts list.
    useEffect(() => {
        api.get("/admin/newsletter/sources").then((r) => setSources(r.data || [])).catch(() => {});
    }, []);
    const loadDrafts = useCallback(() => {
        api.get("/admin/newsletter/drafts").then((r) => setDrafts(r.data || [])).catch(() => {});
    }, []);
    useEffect(() => { loadDrafts(); }, [loadDrafts]);

    // If the parent asked us to open a specific draft (e.g. the user just
    // duplicated a campaign from the History tab), consume the request
    // exactly once so a re-render doesn't keep re-opening it.
    useEffect(() => {
        if (pendingOpenDraft) {
            openDraft(pendingOpenDraft);
            onDraftConsumed?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingOpenDraft]);

    // Recalculate segment count whenever sources selection changes.
    useEffect(() => {
        if (!sources.length) return;
        if (form.sources.length === 0) {
            setCount(sources.reduce((a, s) => a + s.count, 0));
        } else {
            setCount(sources.filter((s) => form.sources.includes(s.source)).reduce((a, s) => a + s.count, 0));
        }
    }, [form.sources, sources]);

    const toggleSource = (s) => {
        setForm((f) => f.sources.includes(s) ? { ...f, sources: f.sources.filter((x) => x !== s) } : { ...f, sources: [...f.sources, s] });
    };

    const setBlocks = (blocks) => setForm((f) => ({ ...f, blocks }));

    // Server-side email preview. Sending BOTH `content` (derived) and
    // `blocks` — the backend uses whichever is present. This is how the
    // preview stays 1:1 with what the recipient will actually get.
    const renderPreview = useCallback(async () => {
        try {
            const { data } = await api.post("/admin/newsletter/campaigns/preview", {
                subject: form.subject, preheader: form.preheader,
                content: blocksToContent(form.blocks),
                blocks: serializeBlocks(form.blocks),
                sources: form.sources,
            });
            return data.html;
        } catch (e) {
            return `<pre style="padding:20px;color:#c00">Erro: ${e?.response?.data?.detail || e.message}</pre>`;
        }
    }, [form.subject, form.preheader, form.blocks, form.sources]);

    const buildPayload = () => ({
        subject: form.subject,
        preheader: form.preheader,
        sources: form.sources,
        content: blocksToContent(form.blocks),
        blocks: serializeBlocks(form.blocks),
        scheduled_at: form.scheduled_at ? localInputToIso(form.scheduled_at) : null,
    });

    // Auto-save: whenever the form changes and there's something worth
    // saving, debounce ~2.5s and PUT/POST to /admin/newsletter/drafts.
    // Uses a ref so quick successive edits collapse into one save.
    const autoSaveTimer = useRef(null);
    const doAutoSave = useCallback(async () => {
        // Never auto-save if there's no meaningful content — avoids
        // creating empty draft rows the admin never intended.
        const hasContent = (form.subject || "").trim() || blocksToContent(form.blocks).trim();
        if (!hasContent) return;
        setAutoSaving(true);
        try {
            const payload = buildPayload();
            if (form.id) {
                await api.put(`/admin/newsletter/drafts/${form.id}`, payload);
            } else {
                const { data } = await api.post("/admin/newsletter/drafts", payload);
                setForm((f) => ({ ...f, id: data.id }));
            }
            setAutoSavedAt(new Date());
            setDirty(false);
            loadDrafts();
        } catch { /* stay dirty, next tick tries again */ }
        finally { setAutoSaving(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.id, form.subject, form.preheader, form.sources, form.blocks, form.scheduled_at]);

    useEffect(() => {
        if (skipNextDirty.current) { skipNextDirty.current = false; return; }
        setDirty(true);
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = setTimeout(() => { doAutoSave(); }, 2500);
        return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.subject, form.preheader, form.sources, form.blocks, form.scheduled_at]);

    const doSaveDraft = async () => {
        // Drafts intentionally have no minimum content — the whole point is
        // to preserve work in progress. Only enforce non-empty state so we
        // never create truly empty drafts.
        if (!form.subject.trim() && !blocksToContent(form.blocks).trim()) {
            return toast.error("Escreva pelo menos o assunto ou algum conteúdo antes de guardar.");
        }
        setSavingDraft(true);
        try {
            const payload = buildPayload();
            if (form.id) {
                await api.put(`/admin/newsletter/drafts/${form.id}`, payload);
                toast.success("Rascunho atualizado");
            } else {
                const { data } = await api.post("/admin/newsletter/drafts", payload);
                setForm((f) => ({ ...f, id: data.id }));
                toast.success("Rascunho guardado");
            }
            loadDrafts();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao guardar rascunho");
        } finally { setSavingDraft(false); }
    };

    const openDraft = async (id) => {
        try {
            const { data } = await api.get(`/admin/newsletter/drafts/${id}`);
            // Legacy drafts (from before this feature) may only have `content` —
            // fall back to the paragraph parser so nothing is lost. Every
            // saved-from-editor draft always has explicit blocks.
            const b = (Array.isArray(data.blocks) && data.blocks.length)
                ? data.blocks.map((x, i) => ({ ...x, id: x.id || `b-${i}-${Math.random().toString(36).slice(2, 8)}` }))
                : [emptyBlock("paragraph")];
            skipNextDirty.current = true;
            setForm({
                id: data.id,
                subject: data.subject || "",
                preheader: data.preheader || "",
                sources: data.sources || [],
                blocks: b,
                scheduled_at: isoToLocalInput(data.scheduled_at),
            });
            setDirty(false);
            setAutoSavedAt(data.updated_at ? new Date(data.updated_at) : null);
            toast.success("Rascunho carregado");
        } catch { toast.error("Não foi possível abrir o rascunho"); }
    };

    const duplicateSource = async (opts) => {
        // opts = { draft_id } | { campaign_id }. Server creates a fresh draft
        // and returns it; we open the copy right away so the admin can start
        // editing without an extra click.
        try {
            const { data } = await api.post("/admin/newsletter/drafts/duplicate", opts);
            toast.success("Cópia criada");
            loadDrafts();
            await openDraft(data.id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao duplicar");
        }
    };

    const deleteDraft = async (id) => {
        if (!window.confirm("Eliminar este rascunho? Esta ação é permanente.")) return;
        try {
            await api.delete(`/admin/newsletter/drafts/${id}`);
            if (form.id === id) setForm(emptyForm());
            loadDrafts();
            toast.success("Rascunho eliminado");
        } catch { toast.error("Erro ao eliminar"); }
    };

    const resetForm = () => {
        skipNextDirty.current = true;
        setForm(emptyForm());
        setDirty(false);
        setAutoSavedAt(null);
    };

    const doSend = async () => {
        if (!form.subject.trim()) return toast.error("Preencha o assunto");
        if (!blocksToContent(form.blocks).trim()) return toast.error("A Nota de Obra tem que ter conteúdo");
        const n = count ?? "todos os";
        const segLabel = form.sources.length ? ` (segmentação: ${form.sources.join(", ")})` : " (todos os subscritores)";
        if (!window.confirm(`Enviar esta campanha para ${n} subscritor(es) ativos${segLabel}? Esta ação é irreversível.`)) return;
        setSending(true);
        try {
            await api.post("/admin/newsletter/campaigns", buildPayload());
            // Publishing from a draft → remove the draft only AFTER the
            // send is safely enqueued. If the delete somehow fails, the draft
            // stays around for the admin to delete manually — never worse.
            if (form.id) {
                try { await api.delete(`/admin/newsletter/drafts/${form.id}`); } catch {}
            }
            toast.success("Campanha em envio. Vamos atualizar o histórico à medida.");
            resetForm();
            loadDrafts();
            onSent?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar"); }
        finally { setSending(false); }
    };

    const doSendTest = async () => {
        if (!form.subject.trim()) return toast.error("Preencha o assunto");
        if (!blocksToContent(form.blocks).trim()) return toast.error("A Nota de Obra tem que ter conteúdo");
        const to = (testEmail || "").trim();
        if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) return toast.error("Indique um email válido para o teste");
        setSendingTest(true);
        try {
            const { data } = await api.post("/admin/newsletter/campaigns/test", {
                subject: form.subject,
                preheader: form.preheader,
                content: blocksToContent(form.blocks),
                blocks: serializeBlocks(form.blocks),
                to,
            });
            toast.success(`Teste enviado para ${data.to}. Message ID: ${data.message_id?.slice(0, 8)}…`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar teste"); }
        finally { setSendingTest(false); }
    };

    return (
        <div className="space-y-6" data-testid="send-campaign">
            {drafts.length > 0 && (
                <DraftList
                    drafts={drafts}
                    editingId={form.id}
                    onOpen={openDraft}
                    onDuplicate={(id) => duplicateSource({ draft_id: id })}
                    onDelete={deleteDraft}
                />
            )}

            <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-2 text-sm text-stone-600 bg-amber-50 border border-amber-200 rounded p-3">
                    <Users className="w-4 h-4 text-amber-600" />
                    <span>Vai enviar para <b>{count ?? "…"}</b> subscritor{count === 1 ? "" : "es"} ativos.</span>
                    {form.id && (
                        <span className="ml-auto text-[10px] uppercase bg-amber-100 text-amber-800 rounded-full px-2 py-0.5" data-testid="editing-draft-pill">
                            A editar rascunho
                        </span>
                    )}
                </div>

                {sources.length > 0 && (
                    <div>
                        <span className="text-xs uppercase tracking-wider text-stone-500 block mb-2">Segmento (origem da subscrição)</span>
                        <p className="text-[11px] text-stone-500 mb-2">Deixe vazio para enviar para todos. Selecione uma ou várias origens para segmentar (ex.: só quem subscreveu na landing de Cascais).</p>
                        <div className="flex flex-wrap gap-2" data-testid="campaign-sources">
                            {sources.map((s) => {
                                const active = form.sources.includes(s.source);
                                return (
                                    <button type="button" key={s.source} onClick={() => toggleSource(s.source)}
                                        className={`text-xs px-3 py-1.5 rounded-full border transition ${active ? "bg-[hsl(var(--forest))] text-white border-[hsl(var(--forest))]" : "bg-white border-stone-300 text-stone-700 hover:border-[hsl(var(--forest))]"}`}
                                        data-testid={`source-${s.source}`}>
                                        {s.source} <span className="opacity-70 font-mono">· {s.count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <label className="block">
                    <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Assunto do email *</span>
                    <input value={form.subject} onChange={(e) => setForm((s) => ({ ...s, subject: e.target.value }))}
                        className="w-full border border-stone-300 rounded p-2" maxLength={120}
                        data-testid="campaign-subject" placeholder="Ex.: Guia rápido antes de remodelar uma cozinha" />
                </label>

                <label className="block">
                    <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Preheader (opcional)</span>
                    <input value={form.preheader} onChange={(e) => setForm((s) => ({ ...s, preheader: e.target.value }))}
                        className="w-full border border-stone-300 rounded p-2" maxLength={140}
                        placeholder="Texto de pré-visualização que aparece na caixa de entrada" data-testid="campaign-preheader" />
                </label>

                <label className="block">
                    <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1 flex items-center gap-2">
                        <CalendarClock className="w-3.5 h-3.5" /> Agendar envio (opcional)
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            type="datetime-local"
                            value={form.scheduled_at || ""}
                            onChange={(e) => setForm((s) => ({ ...s, scheduled_at: e.target.value }))}
                            className="border border-stone-300 rounded p-2 text-sm"
                            data-testid="campaign-scheduled-at"
                        />
                        {form.scheduled_at && (
                            <button type="button" onClick={() => setForm((s) => ({ ...s, scheduled_at: "" }))}
                                className="text-xs text-stone-500 hover:text-red-600 underline" data-testid="campaign-scheduled-clear">
                                Remover agendamento
                            </button>
                        )}
                    </div>
                    <span className="text-[11px] text-stone-500 mt-1 block">
                        Se preenchido, guardar o rascunho é suficiente — o sistema envia automaticamente na data escolhida (hora local). Se deixar vazio, o envio só acontece quando clicar em <b>Enviar</b>.
                    </span>
                </label>

                <div className="pt-1">
                    <span className="text-xs uppercase tracking-wider text-stone-500 block mb-2">Conteúdo *</span>
                    <p className="text-[11px] text-stone-500 mb-3">O template da Nota de Obra aplica automaticamente logo, tipografia, cores, espaçamentos e footer legal — foca-te no conteúdo.</p>
                    <BlogBlockEditor
                        value={form.blocks}
                        onChange={setBlocks}
                        types={["paragraph", "subtitle", "list", "pull_quote", "image", "signature"]}
                        renderPreview={renderPreview}
                    />
                </div>

                <div className="pt-2 border-t border-stone-200">
                    <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Enviar teste (só para si)</span>
                    <p className="text-[11px] text-stone-500 mb-2">Envia uma cópia [TESTE] deste email para o endereço abaixo. Não afeta os subscritores nem o histórico de campanhas.</p>
                    <div className="flex flex-wrap gap-2">
                        <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} type="email"
                            className="flex-1 min-w-[200px] border border-stone-300 rounded p-2 text-sm"
                            placeholder="ola@ohomemdaobra.pt"
                            data-testid="campaign-test-email" />
                        <button onClick={doSendTest} disabled={sendingTest} className="px-4 py-2 border border-[hsl(var(--copper))] text-[hsl(var(--copper))] rounded-full text-sm flex items-center gap-2 disabled:opacity-50" data-testid="campaign-send-test">
                            {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Enviar teste
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                    <button onClick={doSaveDraft} disabled={savingDraft}
                        className="px-4 py-2 border border-stone-300 rounded-full text-sm flex items-center gap-2 disabled:opacity-50"
                        data-testid="campaign-save-draft">
                        {savingDraft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {form.id ? "Atualizar rascunho" : "Guardar rascunho"}
                    </button>
                    <AutoSaveIndicator dirty={dirty} autoSaving={autoSaving} savedAt={autoSavedAt} />
                    {form.id && (
                        <button onClick={resetForm}
                            className="px-4 py-2 border border-stone-300 rounded-full text-sm flex items-center gap-2"
                            data-testid="campaign-new">
                            <Plus className="w-4 h-4" /> Nova
                        </button>
                    )}
                    <div className="flex-1" />
                    <button onClick={doSend} disabled={sending || !count} className="btn-copper !py-2 !px-4 text-sm disabled:opacity-50" data-testid="campaign-send">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar para {count ?? "…"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function DraftList({ drafts, editingId, onOpen, onDuplicate, onDelete }) {
    const fmtSched = (iso) => {
        if (!iso) return null;
        try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
        catch { return null; }
    };
    return (
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid="drafts-list">
            <div className="px-4 py-2 bg-stone-50 border-b border-stone-100 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-stone-500" />
                <span className="text-[11px] uppercase tracking-widest text-stone-500 font-mono">Rascunhos ({drafts.length})</span>
            </div>
            <div className="divide-y divide-stone-100">
                {drafts.map((d) => {
                    const schedLabel = fmtSched(d.scheduled_at);
                    const failed = d.publish_status === "failed";
                    return (
                        <div key={d.id} className={`p-3 flex items-center gap-3 ${editingId === d.id ? "bg-amber-50/40" : ""}`} data-testid={`draft-${d.id}`}>
                            <span className="text-[10px] uppercase bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-mono">Rascunho</span>
                            {schedLabel && !failed && (
                                <span className="text-[10px] uppercase bg-blue-100 text-blue-800 rounded-full px-2 py-0.5 font-mono flex items-center gap-1" data-testid={`draft-scheduled-${d.id}`}>
                                    <CalendarClock className="w-3 h-3" /> {schedLabel}
                                </span>
                            )}
                            {failed && (
                                <span className="text-[10px] uppercase bg-red-100 text-red-800 rounded-full px-2 py-0.5 font-mono" title={d.publish_error || ""} data-testid={`draft-failed-${d.id}`}>
                                    Falhou · abrir para corrigir
                                </span>
                            )}
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm text-stone-900 truncate">{d.subject || <span className="italic text-stone-400">(sem assunto)</span>}</div>
                                <div className="text-[11px] text-stone-500">Atualizado {fmt(d.updated_at)} · {(d.sources || []).length ? d.sources.join(", ") : "todos os subscritores"}</div>
                            </div>
                            <button onClick={() => onOpen(d.id)} className="text-xs px-3 py-1.5 border border-stone-300 rounded flex items-center gap-1" data-testid={`draft-open-${d.id}`}>
                                <Pencil className="w-3.5 h-3.5" /> Abrir
                            </button>
                            <button onClick={() => onDuplicate(d.id)} className="text-xs px-2 py-1.5 border border-stone-300 rounded" title="Duplicar" data-testid={`draft-dup-${d.id}`}>
                                <Copy className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => onDelete(d.id)} className="text-xs px-2 py-1.5 border border-red-200 text-red-600 rounded" data-testid={`draft-del-${d.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/** Subtle indicator next to the Save button — mirrors the pattern of
 *  document editors like Google Docs so admins understand their work is
 *  being preserved even without an explicit click. */
function AutoSaveIndicator({ dirty, autoSaving, savedAt }) {
    if (autoSaving) {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] text-stone-500" data-testid="autosave-indicator">
                <Loader2 className="w-3 h-3 animate-spin" /> A guardar automaticamente…
            </span>
        );
    }
    if (dirty) {
        return <span className="text-[11px] text-stone-400" data-testid="autosave-indicator">Alterações por guardar</span>;
    }
    if (savedAt) {
        try {
            const t = savedAt.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
            return (
                <span className="inline-flex items-center gap-1 text-[11px] text-stone-500" data-testid="autosave-indicator">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Guardado às {t}
                </span>
            );
        } catch { return null; }
    }
    return null;
}

/* ---------- HISTORY ---------- */
function History_({ onDuplicated }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/newsletter/campaigns");
            setItems(data || []);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => {
        load();
        // Poll every 5s so live send progress shows up
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, [load]);

    const openDetail = async (id) => {
        try {
            const { data } = await api.get(`/admin/newsletter/campaigns/${id}`);
            setSelected(data);
        } catch { toast.error("Erro ao carregar detalhe"); }
    };

    const resumeCampaign = async (id) => {
        if (!window.confirm("Retomar o envio desta campanha? Vai usar o limite diário disponível.")) return;
        try {
            const { data } = await api.post(`/admin/newsletter/campaigns/${id}/resume`);
            toast.success(`Retoma agendada (${data.queued_remaining} por enviar).`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Não foi possível retomar"); }
    };

    const duplicateCampaign = async (id) => {
        // Server clones the campaign's editable fields into a fresh draft;
        // the parent then switches to the send tab and auto-opens the copy
        // so the admin can start editing immediately.
        try {
            const { data } = await api.post("/admin/newsletter/drafts/duplicate", { campaign_id: id });
            toast.success("Cópia criada como rascunho");
            onDuplicated?.(data.id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao duplicar");
        }
    };

    return (
        <div>
            <p className="text-sm text-stone-500 mb-4">
                {loading && !items.length ? "A carregar…" : <>{items.length} campanha{items.length === 1 ? "" : "s"} enviada{items.length === 1 ? "" : "s"}.</>}
            </p>
            <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
                {items.map((c) => (
                    <div key={c.id} className="p-4 flex flex-col sm:flex-row sm:items-start gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-stone-900 truncate">{c.subject}</h3>
                                <StatusPill status={c.status} />
                            </div>
                            <div className="text-xs text-stone-500 flex flex-wrap gap-3 mt-1">
                                <span>{fmt(c.created_at)}</span>
                                <span>Enviados: <b>{c.sent_count}/{c.recipient_count}</b></span>
                                {c.failed_count > 0 && <span className="text-red-600">Falhas: {c.failed_count}</span>}
                            </div>
                            <MetricsChips a={c.analytics || {}} />
                        </div>
                        {c.status === "queued_partial" && (
                            <button onClick={() => resumeCampaign(c.id)}
                                className="text-xs px-3 py-1.5 border border-[hsl(var(--copper))] text-[hsl(var(--copper))] rounded flex items-center gap-1 self-start sm:self-auto"
                                data-testid={`campaign-resume-${c.id}`}>
                                <PlayCircle className="w-3.5 h-3.5" /> Continuar envio
                            </button>
                        )}
                        <button onClick={() => duplicateCampaign(c.id)}
                            className="text-xs px-2 py-1.5 border border-stone-300 rounded self-start sm:self-auto"
                            title="Duplicar como rascunho"
                            data-testid={`campaign-dup-${c.id}`}>
                            <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => openDetail(c.id)} className="text-xs px-3 py-1.5 border border-stone-300 rounded self-start sm:self-auto">Ver</button>
                    </div>
                ))}
                {!loading && items.length === 0 && (
                    <div className="p-10 text-center text-stone-500">Ainda sem campanhas. Envie a primeira no separador anterior.</div>
                )}
            </div>

            {selected && (
                <div className="fixed inset-0 bg-stone-950/60 z-50 overflow-y-auto" onClick={() => setSelected(null)}>
                    <div className="min-h-full flex items-start justify-center p-4">
                        <div className="bg-white rounded-xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
                            <div className="p-6 space-y-3">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h2 className="font-display font-bold text-xl flex-1">{selected.subject}</h2>
                                    <StatusPill status={selected.status} />
                                </div>
                                <div className="text-xs text-stone-500 flex flex-wrap gap-3">
                                    <span>Criada: {fmt(selected.created_at)}</span>
                                    {selected.sent_at && <span>Concluída: {fmt(selected.sent_at)}</span>}
                                    <span>Destinatários: <b>{selected.recipient_count}</b></span>
                                    <span>Enviados: <b>{selected.sent_count}</b></span>
                                    {selected.failed_count > 0 && <span className="text-red-600">Falhas: {selected.failed_count}</span>}
                                </div>
                                <MetricsChips a={selected.analytics || {}} />
                                <iframe title="Corpo" srcDoc={selected.html} className="w-full min-h-[500px] bg-white rounded border border-stone-200" />
                                <div className="flex justify-end">
                                    <button onClick={() => setSelected(null)} className="px-6 py-2 border border-stone-300 rounded-full">Fechar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatusPill({ status }) {
    if (status === "sent") return <span className="inline-flex items-center gap-1 text-[10px] uppercase bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5"><CheckCircle2 className="w-3 h-3" /> Enviada</span>;
    if (status === "sent_with_errors") return <span className="inline-flex items-center gap-1 text-[10px] uppercase bg-amber-100 text-amber-800 rounded-full px-2 py-0.5"><AlertCircle className="w-3 h-3" /> Com falhas</span>;
    if (status === "sending") return <span className="inline-flex items-center gap-1 text-[10px] uppercase bg-blue-100 text-blue-800 rounded-full px-2 py-0.5"><Loader2 className="w-3 h-3 animate-spin" /> A enviar</span>;
    if (status === "queued_partial") return <span className="inline-flex items-center gap-1 text-[10px] uppercase bg-purple-100 text-purple-800 rounded-full px-2 py-0.5"><PlayCircle className="w-3 h-3" /> Parcial (limite diário)</span>;
    return <span className="text-[10px] uppercase bg-stone-200 rounded-full px-2 py-0.5">{status || "—"}</span>;
}

// Small analytics chips row for campaign history. Values are absolute + %.
// Aberturas are flagged as "indicativas" because Apple Mail Privacy and Gmail
// prefetch inflate open counts without human interaction.
function MetricsChips({ a }) {
    if (!a || typeof a !== "object") return null;
    return (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="campaign-metrics">
            <Chip label="Entregues" value={a.delivered} pct={a.delivered_pct} tone="emerald" />
            <Chip label="Aberturas" value={a.opened} pct={a.opened_pct} tone="copper" indicative />
            <Chip label="Cliques" value={a.clicked} pct={a.clicked_pct} tone="copper" />
            {a.bounced > 0 && <Chip label="Bounces" value={a.bounced} pct={a.bounced_pct} tone="amber" />}
            {a.complained > 0 && <Chip label="Queixas" value={a.complained} pct={a.complained_pct} tone="red" />}
            {a.unsubscribed > 0 && <Chip label="Unsubs" value={a.unsubscribed} tone="stone" />}
        </div>
    );
}

const CHIP_TONE = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-100",
    copper: "bg-[hsl(var(--copper))]/10 text-[hsl(var(--copper))] border-[hsl(var(--copper))]/20",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-100",
    stone: "bg-stone-100 text-stone-700 border-stone-200",
};

function Chip({ label, value = 0, pct, tone = "stone", indicative = false }) {
    return (
        <span className={`inline-flex items-baseline gap-1 text-[11px] rounded-full border px-2 py-0.5 ${CHIP_TONE[tone]}`}>
            <span className="uppercase tracking-widest text-[9px] font-mono">{label}{indicative ? " (ind.)" : ""}</span>
            <b className="font-mono tabular-nums">{value}</b>
            {typeof pct === "number" && <span className="opacity-70 font-mono tabular-nums">{pct.toFixed(1)}%</span>}
        </span>
    );
}
